import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAssignmentSubmissionsStatus } from '../service';
import { isRetryableQueryError, shouldRetryQuery } from '../utils/queryRetry';
import { submissionIdOf } from '../components/BulkUpload/utils/feedbackRun';

const recovery = new WeakMap();
export const STATUS_READ_TIMEOUT_MS = 30000;
const recoveryFor = query => {
  if (!recovery.has(query)) recovery.set(query, { used: false, offline: !navigator.onLine });
  return recovery.get(query);
};

export const readStatusWithDeadline = async (assignmentId, includeText, signal) => {
  const controller = new AbortController();
  let cancel;
  const cancelled = new Promise((_, reject) => { cancel = reject; });
  const abort = () => {
    cancel(new DOMException('Status read cancelled', 'AbortError'));
    controller.abort();
  };
  if (signal?.aborted) throw new DOMException('Status read cancelled', 'AbortError');
  signal?.addEventListener('abort', abort, { once: true });
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(Object.assign(new Error('Status read timed out. Please retry.'), { status: 408, name: 'TimeoutError' }));
      controller.abort();
    }, STATUS_READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([getAssignmentSubmissionsStatus(assignmentId, { includeText, signal: controller.signal }), deadline, cancelled]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
};

export const runPollInterval = (status, queryState, isActive, isRunRequested) =>
  queryState?.fetchFailureCount > 0 || queryState?.status === 'error'
    ? false
    : isRunRequested || isActive(status) ? 2500 : false;

/** Retry budget belongs to the Query, so additional observers cannot restart it. */
export const useRunStatusQuery = ({ assignmentId, queryKey, enabled, includeText = false, isRunRequested, isActive }) => {
  const client = useQueryClient();
  const queryName = queryKey[0];
  const findQuery = () => client.getQueryCache().find({ queryKey, exact: true });
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const status = await readStatusWithDeadline(assignmentId, includeText, signal);
      const cached = findQuery();
      if (cached) recoveryFor(cached).used = false;
      return status;
    },
    enabled: !!assignmentId && enabled,
    networkMode: 'always',
    retry: shouldRetryQuery,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (status, cached) => runPollInterval(status, cached.state, isActive, isRunRequested),
  });

  useEffect(() => {
    if (!assignmentId || !enabled) return;
    const cached = client.getQueryCache().find({ queryKey: [queryName, assignmentId], exact: true });
    if (!cached) return;
    const state = recoveryFor(cached);
    const offline = () => { state.offline = true; };
    const online = () => {
      if (!state.offline) return;
      state.offline = false;
      if (state.used || !cached.isActive() || cached.state.fetchStatus !== 'idle'
          || cached.state.status !== 'error' || !isRetryableQueryError(cached.state.error)) return;
      state.used = true;
      cached.fetch(undefined, { cancelRefetch: false }).catch(() => {});
    };
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => { window.removeEventListener('offline', offline); window.removeEventListener('online', online); };
  }, [client, queryName, assignmentId, enabled]);

  const refetch = useCallback((options = {}) => {
    const cached = client.getQueryCache().find({ queryKey: [queryName, assignmentId], exact: true });
    if (cached) recoveryFor(cached).used = false;
    return query.refetch({ ...options, cancelRefetch: false });
  }, [client, queryName, assignmentId, query.refetch]);

  const refreshAfterStart = useCallback(async () => {
    await client.cancelQueries({ queryKey: [queryName, assignmentId], exact: true });
    return refetch();
  }, [client, queryName, assignmentId, refetch]);
  return { ...query, refetch, refreshAfterStart };
};

/** Unchanged terminal data belongs to the previous request until a post-accept read. */
export const statusForPendingRun = (status, baseline, field, observed = new Map()) => {
  if (!status?.submissions) return status;
  const before = new Map((baseline?.submissions || []).map(row => [submissionIdOf(row), row[field]]));
  return { ...status, submissions: status.submissions.map(row => {
    const id = submissionIdOf(row);
    const previous = observed.get(id) || before.get(id);
    const current = row[field];
    const terminal = ['READY', 'FAILED', 'SKIPPED', 'EXTRACTED'].includes(current?.status)
      || (field === 'extraction' && !current?.status && !!current?.text);
    if (id && ['NOT_REQUESTED', 'PENDING', 'QUEUED', 'GENERATING', 'EXTRACTING'].includes(current?.status)) observed.set(id, current);
    return terminal && (!previous || JSON.stringify(previous) === JSON.stringify(current))
      ? { ...row, [field]: { ...row[field], status: field === 'feedback' ? 'PENDING' : 'EXTRACTING' } }
      : row;
  }) };
};
